import type {
  AccessCatalog,
  ClientSwitch,
  ClientSwitchField,
  SurfaceKey,
} from "types/interfaces/access";
import type { GrantedLine } from "./permissionsPanelModel";

/** The switches of a client user, and the value that picks a switch's choice. */
export type ClientSwitchState = Partial<Record<ClientSwitchField, boolean>> & {
  integrations_experience?: string;
};

function isOn(state: ClientSwitchState, item: ClientSwitch): boolean {
  return (
    state[item.field] === true &&
    (item.parent == null || state[item.parent] === true)
  );
}

function grantsOf(state: ClientSwitchState, item: ClientSwitch): string {
  if (!item.choice_field) return item.grants;
  const value = state[item.choice_field];
  // A profile without the value has the first choice, as the server defaults it.
  const choice = item.choices.find((c) => c.value === value) ?? item.choices[0];
  return choice?.grants ?? item.grants;
}

/** What a client user's switches open, worded by the catalog; the pages every
 * client user opens come last. */
export function clientGrantedLines(
  catalog: AccessCatalog,
  state: ClientSwitchState,
): GrantedLine[] {
  const lines = catalog.client_user.switches
    .filter((item) => isOn(state, item))
    .map((item) => ({
      key: item.field,
      name: item.name,
      grants: grantsOf(state, item),
    }));
  return [...lines, ...catalog.client_user.always];
}

/** The switches that are off. A sub-option is named only while its parent is on:
 * without the parent it says nothing new. */
export function clientNoAccessNames(
  catalog: AccessCatalog,
  state: ClientSwitchState,
): string[] {
  return catalog.client_user.switches
    .filter(
      (item) =>
        !isOn(state, item) &&
        (item.parent == null || state[item.parent] === true),
    )
    .map((item) => item.name);
}

/** The top-level switches, each with the sub-options that hang off it. */
export function switchTree(
  catalog: AccessCatalog,
): { item: ClientSwitch; children: ClientSwitch[] }[] {
  const all = catalog.client_user.switches;
  return all
    .filter((item) => item.parent == null)
    .map((item) => ({
      item,
      children: all.filter((child) => child.parent === item.field),
    }));
}

/** The page a switch opens in this state: for a switch with choices, the chosen
 * one's. */
function pageOf(
  state: ClientSwitchState,
  item: ClientSwitch,
): SurfaceKey | null {
  if (item.choice_field && item.choices.length > 0) {
    const value = state[item.choice_field];
    const choice =
      item.choices.find((c) => c.value === value) ?? item.choices[0];
    return choice.surface ?? item.surface ?? null;
  }
  return item.surface ?? null;
}

/** What the signed-in user may only look at on a client user's Access step. */
export interface SwitchLocks {
  switches: Set<ClientSwitchField>;
  /** Switches whose choice stays as it is. */
  choices: Set<ClientSwitchField>;
  /** What the step names as listed for reference, in the order shown. */
  names: string[];
}

/** A switch stays as it is for someone whose own group does not open its page,
 * and so does a choice between pages they do not all open - the server refuses
 * both the same way. */
export function switchLocks(
  catalog: AccessCatalog,
  state: ClientSwitchState,
  opens: (surface: SurfaceKey) => boolean,
): SwitchLocks {
  const locks: SwitchLocks = {
    switches: new Set(),
    choices: new Set(),
    names: [],
  };
  for (const { item, children } of switchTree(catalog)) {
    const page = pageOf(state, item);
    const itemLocked = page != null && !opens(page);
    if (itemLocked) {
      locks.switches.add(item.field);
      locks.names.push(item.name);
    }
    const closedChoices = item.choices.filter(
      (choice) => choice.surface != null && !opens(choice.surface),
    );
    if (closedChoices.length > 0) {
      locks.choices.add(item.field);
      if (!itemLocked) locks.names.push(...closedChoices.map((c) => c.label));
    }
    for (const child of children) {
      if (child.surface != null && !opens(child.surface)) {
        locks.switches.add(child.field);
        if (!itemLocked) locks.names.push(child.name);
      }
    }
  }
  return locks;
}
